import React, { useCallback, useMemo, useContext } from 'react';
import { QueryClient, UseQueryResult, useQueryClient } from 'react-query';
import { useConfirm } from 'material-ui-confirm';

import { AppContext } from 'views/App/context';
import { translate as t } from 'hooks/useTranslations';
import useRiskPoolOrganizations from './_data_sources/useRiskPoolOrganizations';
import useManageRiskPoolOrganizations from './_data_sources/useManageRiskPoolOrganizations';
import type { RelatedOrganization, CreateRelatedOrganizationRequest } from './_types';
import { ApiCollectionResponse } from 'types/api_response';
import { ServerErrorResponse } from 'data_sources/constants';

type RiskPoolSharingProviderProps = {
  children: React.ReactNode;
};

export type RiskPoolSharingProviderResponse = {
  createRelation: (
    data: CreateRelatedOrganizationRequest,
    onSuccess?: (data: any) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => void;
  deleteRelation: (
    childOrgId: string,
    onSuccess?: () => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => void;
  queryClient: QueryClient | null;
  organizations: RelatedOrganization[];
  organizationsQuery: UseQueryResult<ApiCollectionResponse<RelatedOrganization>, ServerErrorResponse>;
};

export const RiskPoolSharingContext = React.createContext<RiskPoolSharingProviderResponse>(
  {} as RiskPoolSharingProviderResponse
);

const RiskPoolSharingProvider: ({ children }: RiskPoolSharingProviderProps) => any = ({ children }) => {
  const { currentOrganizationId } = useContext(AppContext);
  const orgId = currentOrganizationId;
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const organizationsData = useRiskPoolOrganizations(orgId);
  const { create: createRelation, destroy: deleteRelation } = useManageRiskPoolOrganizations();

  const organizations = useMemo(
    () => (organizationsData.data?.content ? organizationsData.data.content : []),
    [organizationsData.data]
  );

  const handleCreateRelation = useCallback(
    (
      data: CreateRelatedOrganizationRequest,
      onSuccess?: (data: any) => void,
      onError?: (errors: ServerErrorResponse) => void
    ) => {
      createRelation(
        orgId,
        data,
        (responseData: any) => {
          queryClient.invalidateQueries(['riskPoolOrganizations']);
          if (onSuccess) onSuccess(responseData);
        },
        (errors: ServerErrorResponse) => {
          if (onError) onError(errors);
        }
      );
    },
    [createRelation, orgId, queryClient]
  );

  const handleDeleteRelation = useCallback(
    (childOrgId: string, onSuccess?: () => void, onError?: (errors: ServerErrorResponse) => void) => {
      confirm({
        title: t('riskPoolSharing.delete.title', 'Remove Organization'),
        description: t('riskPoolSharing.delete.confirm'),
        confirmationText: t('riskPoolSharing.delete.confirmationText', 'Remove'),
        cancellationText: t('riskPoolSharing.delete.cancellationText', 'Cancel'),
      })
        .then(() => {
          // Optimistically update the cache before API call
          const previousData = queryClient.getQueryData<ApiCollectionResponse<RelatedOrganization>>(['riskPoolOrganizations', orgId]);

          if (previousData) {
            queryClient.setQueryData<ApiCollectionResponse<RelatedOrganization>>(
              ['riskPoolOrganizations', orgId],
              {
                ...previousData,
                content: previousData.content.filter((org) => org.id !== childOrgId),
                count: (previousData.count || previousData.content.length) - 1,
              }
            );
          }

          deleteRelation(
            orgId,
            childOrgId,
            () => {
              // Refetch to ensure data is in sync with server
              queryClient.invalidateQueries(['riskPoolOrganizations']);
              if (onSuccess) onSuccess();
            },
            (errors: ServerErrorResponse) => {
              // Rollback optimistic update on error
              queryClient.invalidateQueries(['riskPoolOrganizations']);
              if (onError) onError(errors);
            }
          );
        })
        .catch(() => {
          // User cancelled
        });
    },
    [confirm, orgId, deleteRelation, queryClient]
  );

  const contextValue: RiskPoolSharingProviderResponse = useMemo(
    () => ({
      createRelation: handleCreateRelation,
      deleteRelation: handleDeleteRelation,
      queryClient,
      organizationsQuery: organizationsData,
      organizations: organizations,
    }),
    [handleCreateRelation, handleDeleteRelation, queryClient, organizationsData, organizations]
  );

  return <RiskPoolSharingContext.Provider value={contextValue}>{children}</RiskPoolSharingContext.Provider>;
};

export default RiskPoolSharingProvider;